import React, { useEffect, useState } from 'react';
import AlpacaStatus from './AlpacaStatus';
import './ForwardTest.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const cleanEnum = (s) => {
  if (!s) return '—';
  return String(s).replace(/^Order(Side|Status|Type)\./, '');
};

const ForwardTest = () => {
  const [orders, setOrders] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const [ordersRes, logRes] = await Promise.all([
        fetch(`${API_URL}/api/alpaca/orders?limit=100`),
        fetch(`${API_URL}/api/alpaca/rotation-log`),
      ]);
      const ordersJson = await ordersRes.json();
      const logJson = await logRes.json();
      if (ordersJson.status === 'success') setOrders(ordersJson.orders || []);
      if (logJson.status === 'success') setLog(logJson.log || []);
      setError(null);
    } catch (e) {
      setError('Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="ft-page" data-testid="forward-test-page">
      <div className="ft-container">
        <div className="ft-hero">
          <div className="ft-kicker">FLIP FLOP SYSTEM · LIVE LEDGER</div>
          <h1 className="ft-title">The Serene System — Live</h1>
          <p className="ft-sub">
            Real-time execution ledger of the locked 150/50/20 rotation. Signals computed
            on <code>/backtest2</code> fire at <strong>9:30 AM ET</strong> every trading day
            into an Alpaca paper account; the full broker ledger and signal-vs-execution
            reconciliation live below.
          </p>
          <div className="ft-nav">
            <a href="/backtest2" className="ft-back-link" data-testid="ft-back-link">
              ← Back to /backtest2
            </a>
            <span style={{ margin: '0 0.6rem', color: '#475569' }}>·</span>
            <a href="/ffsystemlive2" className="ft-back-link" data-testid="ft-ab-link">
              View SPMO A/B Simulation →
            </a>
          </div>
        </div>

        <AlpacaStatus />

        <section className="ft-section" data-testid="ft-rotation-log-section">
          <div className="ft-section-header">
            <h2 className="ft-section-title">Rotation Decision Log</h2>
            <span className="ft-section-count">{log.length} entries</span>
          </div>
          {loading && log.length === 0 ? (
            <div className="ft-empty">Loading decisions…</div>
          ) : error ? (
            <div className="ft-error">{error}</div>
          ) : log.length === 0 ? (
            <div className="ft-empty">
              No rotations yet. The scheduler will fire next at 9:30 AM ET on the next weekday,
              or click <strong>Execute Today's Signal</strong> above.
            </div>
          ) : (
            <div className="ft-table-wrap">
              <table className="ft-table" data-testid="ft-rotation-log-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Trigger</th>
                    <th>Signal</th>
                    <th>Regime</th>
                    <th>Action</th>
                    <th>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((entry, idx) => {
                    const s = entry.signal || {};
                    const r = entry.result || {};
                    const rotated = r.rotated;
                    return (
                      <tr key={idx}>
                        <td className="ft-mono">{fmtDate(entry.timestamp)}</td>
                        <td>
                          <span
                            className={`ft-tag ${
                              entry.trigger === 'scheduler' ? 'ft-tag-auto' : 'ft-tag-manual'
                            }`}
                          >
                            {entry.trigger}
                          </span>
                        </td>
                        <td className="ft-mono">
                          {s.action} {s.next_target}
                        </td>
                        <td>
                          <span
                            className={`ft-tag ${
                              s.regime === 'RISK-ON' ? 'ft-tag-on' : 'ft-tag-off'
                            }`}
                          >
                            {s.regime || '—'}
                          </span>
                        </td>
                        <td className="ft-mono">
                          {rotated
                            ? `${r.previous_symbol || '—'} → ${r.target_symbol}`
                            : `HOLD ${r.target_symbol || s.current_holding || '—'}`}
                        </td>
                        <td className={rotated ? 'ft-ok' : 'ft-muted'}>
                          {rotated
                            ? `Rotated · ${fmtMoney(r.notional_allocated)}`
                            : r.reason || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="ft-section" data-testid="ft-orders-section">
          <div className="ft-section-header">
            <h2 className="ft-section-title">Alpaca Order Ledger</h2>
            <span className="ft-section-count">{orders.length} orders</span>
          </div>
          {loading && orders.length === 0 ? (
            <div className="ft-empty">Loading orders…</div>
          ) : orders.length === 0 ? (
            <div className="ft-empty">
              No orders yet in this paper account. Fire a rotation to begin.
            </div>
          ) : (
            <div className="ft-table-wrap">
              <table className="ft-table" data-testid="ft-orders-table">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th>Qty / Notional</th>
                    <th>Filled Qty</th>
                    <th>Fill Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id}>
                      <td className="ft-mono">{fmtDate(o.submitted_at)}</td>
                      <td className="ft-mono ft-symbol">{o.symbol}</td>
                      <td>
                        <span
                          className={`ft-tag ${
                            cleanEnum(o.side) === 'BUY' ? 'ft-tag-on' : 'ft-tag-off'
                          }`}
                        >
                          {cleanEnum(o.side)}
                        </span>
                      </td>
                      <td className="ft-mono">
                        {o.qty !== null && o.qty !== undefined
                          ? `${Number(o.qty).toFixed(4)} sh`
                          : fmtMoney(o.notional)}
                      </td>
                      <td className="ft-mono">
                        {o.filled_qty ? Number(o.filled_qty).toFixed(4) : '0'}
                      </td>
                      <td className="ft-mono">
                        {o.filled_avg_price ? fmtMoney(o.filled_avg_price) : '—'}
                      </td>
                      <td>
                        <span
                          className={`ft-tag ${
                            ['FILLED'].includes(cleanEnum(o.status))
                              ? 'ft-tag-on'
                              : ['CANCELED', 'REJECTED', 'EXPIRED'].includes(
                                  cleanEnum(o.status),
                                )
                              ? 'ft-tag-off'
                              : 'ft-tag-pending'
                          }`}
                        >
                          {cleanEnum(o.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ForwardTest;
